/**
 * WorkspaceRoleGuard Component
 * Guards routes based on workspace role (ADMIN or OWNER)
 *
 * Unlike RoleGuard (which checks global user.role), this guard checks
 * the user's role within the currently selected workspace.
 *
 * Handles loading state to prevent false redirects on hard refresh.
 */

import { Navigate } from 'react-router-dom';
import { useWorkspaces } from '@/features/workspaces/hooks';
import { useIsWorkspaceAdmin } from '@/features/workspaces/hooks/useIsWorkspaceAdmin';

export interface WorkspaceRoleGuardProps {
  /** Child components to render when authorized */
  children: React.ReactNode;
  /** Path to redirect to when unauthorized (defaults to /unauthorized) */
  fallbackPath?: string;
}

export const WorkspaceRoleGuard: React.FC<WorkspaceRoleGuardProps> = ({
  children,
  fallbackPath = '/unauthorized',
}) => {
  const { isLoading } = useWorkspaces();
  const isWorkspaceAdmin = useIsWorkspaceAdmin();

  // Do NOT redirect while workspace data is still loading --
  // useIsWorkspaceAdmin() returns false until the workspace list arrives.
  if (isLoading) {
    return null;
  }

  if (!isWorkspaceAdmin) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};
